// Markers over the pieces that are doing industrial work.
//
// These used to be the whole explanation surface: hover for a panel, click to
// unfold controls, forge lists and policy toggles inside. That failed as
// *furniture* rather than as information — the panels sat on top of the very
// squares they described and swallowed the clicks meant for them, so reading
// about a piece and playing near it were the same gesture. All of it moved to
// the inspector, and what is left here is the one job the board still needs:
// saying *where* something is happening, at a glance, without being clicked.
//
// Every element is `pointerEvents: none`. That is the point of the file now,
// so it is asserted rather than assumed — nothing here can ever take a click
// back off the board. It follows that nothing here carries a hover card
// either: a card is a hover, a hover needs a pointer, and this layer refuses
// one. What a piece is doing is said on the inspector card.
//
// DOM rather than more geometry, still deliberately: this is text, and text in
// a 3D scene at a fixed 3/4 pitch is either unreadable or enormous. Positions
// come from `projectSquare`, polled on the renderer's own animation frame —
// never on state change, because the camera drags and snaps independently of
// state and a marker that lags its piece is worse than none.

import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import type { Badge, HealthBar, Overlays, Side, UpgradeMark } from "src/api";
import type { BoardCanvasHandle } from "./BoardCanvas";
import { styled, useOverlayClip, useTheme } from "./kit";

interface Props {
  /** The badges and the bars, as the view says them (`SessionView.overlays`). */
  overlays: Overlays;
  board: RefObject<BoardCanvasHandle | null>;
  /** Which side this client acts for; its own pieces are tinted as its own. */
  localSide: Side;
}

interface Point {
  x: number;
  y: number;
}
type Points = Record<number, Point>;

/** How far above the square's anchor a bar floats, and how wide it is. */
const HEALTH_LIFT = 26;
const HEALTH_WIDTH = 34;

/** Where an upgrade's pip sits: up and right of the anchor, clear of the bar. */
const UPGRADE_LIFT = 38;
const UPGRADE_SHIFT = 14;

/**
 * An upgrade: a small brand diamond over the piece, which the renderer has
 * also drawn a little larger. The row's name is on the inspector card — this
 * only says *this one is not plain*, at a glance, on either side.
 */
const Pip = styled("div")(({ theme }) => ({
  position: "absolute",
  transform: "translate(-50%, -50%) rotate(45deg)",
  width: 7,
  height: 7,
  borderRadius: 1,
  background: theme.kit.color.brand,
  border: `1px solid ${theme.kit.color.shadow}`,
  pointerEvents: "none"
}));

/**
 * The layer: the whole viewport, clipped to what the chrome leaves free.
 *
 * `z.boardOverlay` is the board's own rung, and each marker sorts *within* it
 * by screen-y — which is why `BoardAnchored` derives its ceiling from the gap
 * to the next rung rather than from a number written down twice.
 */
const Layer = styled("div")(({ theme }) => ({
  position: "fixed",
  inset: 0,
  pointerEvents: "none",
  zIndex: theme.kit.z.boardOverlay
}));

/**
 * A wound, as a bar over the square.
 *
 * **Drawn under the badges**, deliberately: a badge names a job and a bar is a
 * reading, so where they collide the thing that says what a piece is *for*
 * wins.
 */
const Bar = styled("div")(({ theme }) => ({
  position: "absolute",
  transform: "translate(-50%, -50%)",
  width: HEALTH_WIDTH,
  height: 5,
  borderRadius: 2,
  background: `color-mix(in srgb, ${theme.kit.color.ground} 80%, transparent)`,
  border: `1px solid ${theme.kit.color.shadow}`,
  overflow: "hidden",
  // Two segments side by side: the health, then the plate a shot has to chew
  // through before it reaches the health.
  display: "flex",
  pointerEvents: "none"
}));

const BarFill = styled("div")({ height: "100%", flex: "none" });

/**
 * A badge: a strip of pips and a short reading, on the panel's own glass.
 *
 * Panel fill with alpha and a line border, like every floating surface in this
 * interface — a badge is the smallest one there is. The border is what carries
 * the state, so it is the only thing here that changes colour.
 */
const Mark = styled("div")(({ theme }) => ({
  position: "absolute",
  transform: "translate(-50%, -50%)",
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.xs,
  padding: "1px 5px",
  borderRadius: theme.kit.radius.sm,
  background: `color-mix(in srgb, ${theme.kit.color.panel} 85%, transparent)`,
  border: `1px solid ${theme.kit.color.line}`,
  fontFamily: theme.kit.font.display,
  fontVariantNumeric: "tabular-nums",
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: "nowrap",
  color: theme.kit.color.ink,
  // Never clickable. The board is underneath and the board is what the click
  // is for.
  pointerEvents: "none"
}));

function samePoints(a: Points, b: Points): boolean {
  const keys = Object.keys(b);
  if (Object.keys(a).length !== keys.length) return false;
  for (const key of keys) {
    const one = a[Number(key)];
    const two = b[Number(key)];
    if (!one || one.x !== two.x || one.y !== two.y) return false;
  }
  return true;
}

export function PieceOverlays({ overlays, board, localSide }: Props) {
  // What the chrome is covering, from the inset registry — the one geometry
  // source (PLAN-UI §28.1). Nothing here may paint under it.
  const clip = useOverlayClip();
  // Two lists, built apart on the engine's side: a badge is offered to a piece
  // with something to say and a bar is owed to every piece that has been hit —
  // including one whose badge is about something else entirely.
  const { badges, health: wounds, upgraded } = overlays;
  const [points, setPoints] = useState<Points>({});

  // Every list is projected, since a hurt or upgraded piece need not carry a badge.
  const squares = [
    ...new Set([
      ...badges.map((badge) => badge.square),
      ...wounds.map((hurt) => hurt.square),
      ...upgraded.map((mark) => mark.square)
    ])
  ].join(",");
  const latest = useRef(squares);
  latest.current = squares;

  const project = useCallback(() => {
    const projectSquare = board.current?.projectSquare;
    if (!projectSquare) return;
    const next: Points = {};
    for (const raw of latest.current.split(",")) {
      if (!raw) continue;
      const square = Number(raw);
      next[square] = projectSquare(square);
    }
    // Only re-render when something actually moved, or a dragged camera sets
    // state sixty times a second for no visible reason.
    setPoints((previous) => (samePoints(previous, next) ? previous : next));
  }, [board]);

  useEffect(() => board.current?.onFrame(project), [board, project]);

  // The renderer only calls back on frames it drew, and a marker appearing on
  // a settled board draws nothing. So place new ones directly as well.
  useEffect(() => project(), [project, squares]);

  return (
    <Layer
      // `clipPath` in `style`, not `sx`: it changes whenever a piece of chrome
      // moves, and emotion mints a permanent class per distinct `sx` value.
      style={{ clipPath: clip }}
    >
      {wounds.map((hurt) => {
        const point = points[hurt.square];
        if (!point) return null;
        return (
          <HealthBarMark
            key={`hp-${hurt.square}`}
            hurt={hurt}
            point={point}
          />
        );
      })}
      {upgraded.map((mark) => {
        const point = points[mark.square];
        if (!point) return null;
        return <UpgradePip key={`up-${mark.square}`} mark={mark} point={point} />;
      })}
      {badges.map((badge) => {
        const point = points[badge.square];
        if (!point) return null;
        return (
          <Marker
            key={badge.square}
            badge={badge}
            point={point}
            mine={badge.owner === localSide}
          />
        );
      })}
    </Layer>
  );
}

/**
 * One piece's wounds and its armour, as a bar over its square.
 *
 * The bar is the piece's whole pool: the health segment first, coloured by how
 * much of it is left, then the plate in gunmetal — the thing a turret grinds
 * before anything reaches the body, so a plated piece reads as a longer bar
 * with a metal end rather than as a whole one.
 *
 * The colour is the *state*, never the owner — the rust end is "about to die"
 * on both armies, and tinting by side would mean learning two colour languages
 * to read one fact. Everything positional goes in `style` rather than `sx`,
 * the rule this file already pays for: `sx` mints a permanent emotion class
 * per distinct value, and this re-projects every frame.
 */
function HealthBarMark({
  hurt,
  point
}: {
  hurt: HealthBar;
  point: Point;
}) {
  const { color } = useTheme().kit;
  // Three bands rather than a gradient: a bar that shades continuously says
  // "somewhat hurt", and the only readings that change a decision are *fine*,
  // *hurt* and *one more hit*. Cream, brand, rust — the language's own ramp,
  // and there is no green in it.
  const fill =
    hurt.fraction > 0.6 ? color.cream : hurt.fraction > 0.3 ? color.brand : color.rust;
  // Shares of one bar: the plate has no maximum of its own, so the only honest
  // scale is the whole pool a shot has to get through.
  const pool = hurt.full + hurt.plate;

  return (
    <Bar
      data-testid={`health-${hurt.square}`}
      data-health={hurt.fraction.toFixed(2)}
      data-plate={hurt.plate}
      style={{
        left: point.x,
        top: point.y - HEALTH_LIFT,
        // Under its own badge but sorted with everything else on the board, so
        // a bar on a near square is not painted over by a far one.
        zIndex: Math.max(1, Math.round(point.y)) - 1
      }}
    >
      <BarFill style={{ width: `${(hurt.left / pool) * 100}%`, background: fill }} />
      {hurt.plate > 0 && (
        <BarFill
          style={{
            // Off the health's own end, so the wound is the gap between them.
            marginLeft: `${((hurt.full - hurt.left) / pool) * 100}%`,
            width: `${(hurt.plate / pool) * 100}%`,
            background: color.lineHi
          }}
        />
      )}
    </Bar>
  );
}

function Marker({
  badge,
  point,
  mine
}: {
  badge: Badge;
  point: Point;
  mine: boolean;
}) {
  const { color } = useTheme().kit;
  // The border is the state, in three steps: a decision waiting on you, a
  // machine that can fire, and everything else — dimmer for somebody else's.
  const edge = badge.needsSetup
    ? color.brand
    : badge.ready
      ? color.cream
      : mine
        ? color.line
        : `color-mix(in srgb, ${color.line} 60%, transparent)`;

  return (
    <Mark
      data-testid={`badge-${badge.square}`}
      data-badge-kind={badge.kind}
      // "There is a decision waiting on you here." The border says it; this
      // says it to a test. A foundry with nothing in it is waiting on
      // material rather than on a choice, and does not carry it.
      data-prompt={badge.needsSetup ? "1" : "0"}
      // Everything that moves goes in `style`, never in `sx`: `sx` compiles to
      // a generated class and emotion mints a permanent one per distinct
      // value, so a marker that re-projects every frame grew the document by
      // hundreds of stylesheet rules a move. The border is here too because it
      // is per-badge state, not per-badge geometry — one mechanism per element.
      style={{
        left: point.x,
        top: point.y,
        borderColor: edge,
        // Depth-sorted. These all sit on the board plane under an orthographic
        // camera at a fixed pitch, so lower on screen *is* nearer the viewer.
        zIndex: Math.max(1, Math.round(point.y))
      }}
    >
      {badge.pips.map((pip, index) => (
        <span
          key={index}
          data-testid={`pip-${pip.glyph}`}
          // The tone is what the pip is doing, and it is data — so `style`,
          // for the reason the position is. Two states, not three: a machine
          // that is *doing* something and one that is not, which is the only
          // distinction that changes what you would click next.
          style={{ color: pip.tone === "waiting" ? color.cream : color.brand }}
        >
          {pip.glyph}
        </span>
      ))}
      {badge.short && <span>{badge.short}</span>}
    </Mark>
  );
}

function UpgradePip({ mark, point }: { mark: UpgradeMark; point: Point }) {
  return (
    <Pip
      data-testid={`upgrade-${mark.square}`}
      data-variant={mark.variant}
      style={{
        left: point.x + UPGRADE_SHIFT,
        top: point.y - UPGRADE_LIFT,
        // Sorted with the bars and badges, under the square's own badge.
        zIndex: Math.max(1, Math.round(point.y)) - 1
      }}
    />
  );
}
